import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { OficioMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/oficio-medico/oficio-medico.service';
import { ReportOficioMedicoRequest, ReportOficioMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/oficio-medico/oficio-medico.model';
import { OficioMedicoJobService } from '@/core/services/oficio-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportOficioMedicoRequest;
type Resp = ReportOficioMedicoResponse;

interface OficioMedicoFormControls {
    conselhoDestino: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono: Ofício para Médico (acuse de recebimento de solicitação
 * de transferência/inscrição secundária). Identificação do médico via nuCrm/sgUf
 * (PessoaService). "Conselho de destino" é opcional: se não informado, o backend
 * tenta resolvê-lo a partir do histórico de situação do médico.
 *
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só resolvemos a identificação do médico
 * e montamos o request.
 */
@Component({
    selector: 'app-oficio-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputTextModule, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './oficio-medico.html',
    styleUrl: './oficio-medico.scss'
})
export class ReportOficioMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly formBuilder = inject(FormBuilder);
    private readonly oficioService = inject(OficioMedicoService);
    readonly oficioJobService = inject(OficioMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.oficioJobService.job$;

    form: FormGroup<OficioMedicoFormControls> = this.formBuilder.group<OficioMedicoFormControls>({
        conselhoDestino: new FormControl<string | null>('')
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.oficioJobService;
    }

    /**
     * Monta o request a partir da pessoa selecionada (nuCrm/sgUf) e do conselho de destino
     * opcional do formulário; retorna null (e avisa) se faltar CRM/UF.
     */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.form.invalid) return null;

        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o ofício.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        const conselhoDestino = this.form.getRawValue().conselhoDestino?.trim();

        return {
            nuCrm,
            sgUf,
            ...(conselhoDestino ? { conselhoDestino } : {})
        };
    }

    protected exportar(request: Req) {
        return this.oficioService.exportarPdf(request);
    }

    /** Reseta o conselho de destino e limpa o job/preview corrente. */
    limparFormulario(): void {
        this.form.reset({ conselhoDestino: '' });
        this.limparJob();
    }
}
